import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Users, 
  MapPin, 
  Calendar, 
  Heart, 
  Brain, 
  TestTube,
  Target,
  Shield
} from 'lucide-react';

const Home = () => {
  const stats = [
    { number: '20,000', label: 'Target Participants', icon: Users },
    { number: '3', label: 'Study Phases', icon: MapPin },
    { number: '20', label: 'Years Follow-up', icon: Calendar },
    { number: '18-29', label: 'Age Range', icon: Heart }
  ];

  const outcomes = [
    { title: 'Early Onset Cancer', description: 'Understanding cancer risk factors in young adults including colorectal, pancreatic, kidney and thyroid cancers' },
    { title: 'Type 2 Diabetes', description: 'Identifying early markers and prevention strategies for T2DM in young adults' },
    { title: 'Post-Viral Syndromes', description: 'Studying long-term effects of COVID-19 and other viral infections' },
    { title: 'Mental Health Disorders', description: 'Anxiety disorders, eating disorders, and psychosis in emerging adulthood' },
    { title: 'Chronic Physical Conditions', description: 'Early detection and prevention of various chronic illnesses' },
    { title: 'Brain & Body Health', description: 'Comprehensive MRI-based assessment of neurological and physical health' }
  ];

  const protocols = [
    { title: 'Advanced MRI Imaging', description: 'Ultra-high gradient MRI of brain and body (sMRI, dMRI, fMRI, cMRI, pMRI)', icon: Brain },
    { title: 'Biospecimen Collection', description: 'Blood biomarkers, hair, urine, and saliva samples', icon: TestTube },
    { title: 'Ocular Imaging', description: 'State-of-the-art eye imaging and vision assessments', icon: Target },
    { title: 'Health Records', description: '20-year follow-up through electronic health records', icon: Shield }
  ];

  return (
    <div className="home">
      {/* Hero Section */}
      <section className="hero" style={{ 
        backgroundImage: 'linear-gradient(135deg, rgba(30, 64, 175, 0.9) 0%, rgba(59, 130, 246, 0.9) 100%), url(/images/hero-bg.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed'
      }}>
        <div className="container">
          <motion.div 
            className="hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1>Shape the Future of Health</h1>
            <p>
              Join Canada's most comprehensive health study for young adults. 
              Help us understand and prevent chronic diseases that are increasingly 
              affecting people in their 20s and 30s.
            </p>
            <div className="hero-buttons">
              <Link to="/participate" className="btn btn-primary">
                Get Involved
              </Link>
              <Link to="/about" className="btn btn-secondary">
                Learn More
              </Link>
            </div>
            <div className="hero-stats">
              <div className="hero-stat">
                <span className="hero-stat-number">2,000</span>
                <span className="hero-stat-label">Phase 1 Goal</span>
              </div>
              <div className="hero-stat">
                <span className="hero-stat-number">18-29</span>
                <span className="hero-stat-label">Age Range</span>
              </div>
              <div className="hero-stat">
                <span className="hero-stat-number">20</span>
                <span className="hero-stat-label">Years Follow-up</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats">
        <div className="container">
          <div className="stats-grid">
            {stats.map((stat, index) => (
              <motion.div 
                key={stat.label}
                className="stat-item"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <stat.icon size={48} style={{ color: '#1E40AF', marginBottom: '1rem' }} />
                <span className="stat-number">{stat.number}</span>
                <div className="stat-label">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title">Why This Study Matters</h2>
            <p className="section-subtitle">
              The Canadian Biobank is a groundbreaking research initiative 
              The Canadian Biobank aims to follow 20,000 young adults across Canada for 15 years to 
              understand how health develops in early adulthood.
            </p>
            
            <div className="grid grid-2" style={{ marginTop: '3rem' }}>
              <div className="card">
                <h3 style={{ color: '#1E40AF', marginBottom: '1rem' }}>The Challenge</h3>
                <p>
                  Many serious health conditions like cancer, diabetes, and mental 
                  health disorders are increasingly affecting young adults, but we 
                  don't fully understand why or how to prevent them.
                </p>
              </div>
              <div className="card">
                <h3 style={{ color: '#1E40AF', marginBottom: '1rem' }}>Our Solution</h3>
                <p>
                  By studying a large group of young adults over time, we can identify 
                  early warning signs and develop better prevention and treatment 
                  strategies for future generations.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Protocols Section */}
      <section className="section" style={{ background: '#f8fafc' }}>
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title">What's Involved</h2>
            <p className="section-subtitle">
              Our comprehensive assessment includes cutting-edge medical imaging, 
              biomarker analysis, and cognitive testing.
            </p>
            
            <div className="grid grid-4">
              {protocols.map((protocol, index) => (
                <motion.div 
                  key={protocol.title}
                  className="card"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <protocol.icon size={32} style={{ color: '#1E40AF', marginBottom: '1rem' }} />
                  <h3 style={{ marginBottom: '0.5rem' }}>{protocol.title}</h3>
                  <p>{protocol.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Participate Section */}
      <section className="section" style={{ background: '#f8fafc' }}>
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="section-title">Why Your Participation Matters</h2>
            <p className="section-subtitle">
              Young adults are experiencing a concerning rise in chronic diseases. 
              Your participation helps us understand why and develop better prevention strategies.
            </p>
            
            <div className="grid grid-3" style={{ marginTop: '2rem' }}>
              <div className="card text-center">
                <Heart size={48} style={{ color: '#1E40AF', marginBottom: '1rem' }} />
                <h3 style={{ color: '#1E40AF', marginBottom: '1rem' }}>Free Health Assessment</h3>
                <p>Comprehensive health evaluation including advanced MRI scans and blood tests worth thousands of dollars.</p>
              </div>
              <div className="card text-center">
                <Brain size={48} style={{ color: '#1E40AF', marginBottom: '1rem' }} />
                <h3 style={{ color: '#1E40AF', marginBottom: '1rem' }}>Contribute to Science</h3>
                <p>Help researchers understand health patterns in young adults and develop better prevention strategies.</p>
              </div>
              <div className="card text-center">
                <Shield size={48} style={{ color: '#1E40AF', marginBottom: '1rem' }} />
                <h3 style={{ color: '#1E40AF', marginBottom: '1rem' }}>Long-term Impact</h3>
                <p>Your participation could lead to breakthrough discoveries that benefit millions of young adults worldwide.</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Outcomes Section */}
      <section className="section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title">Health Outcomes We're Studying</h2>
            <p className="section-subtitle">
              Our research focuses on conditions that significantly impact young adults 
              and their long-term health outcomes.
            </p>
            
            <div className="grid grid-3">
              {outcomes.map((outcome, index) => (
                <motion.div 
                  key={outcome.title}
                  className="card"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <h3 style={{ color: '#1E40AF', marginBottom: '0.5rem' }}>{outcome.title}</h3>
                  <p>{outcome.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section" style={{ background: 'linear-gradient(135deg, #1E40AF 0%, #3B82F6 100%)', color: 'white' }}>
        <div className="container text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 style={{ color: 'white', marginBottom: '1rem' }}>Ready to Make a Difference?</h2>
            <p style={{ fontSize: '1.2rem', marginBottom: '2rem', opacity: 0.9 }}>
              Join thousands of young adults who are helping shape the future of healthcare. 
              Your participation could lead to life-saving discoveries.
            </p>
            <Link to="/participate" className="btn" style={{ 
              background: 'white', 
              color: '#1E40AF',
              border: 'none'
            }}>
              Join the Study Today
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
